import {
  getAdobeVisitorIdentifiers,
  getAdobeAnalyticsUser,
} from '@elsevier-health/osmosis-shared/adobeAnalytics';
import type { AdobeAnalyticsUser } from '@elsevier-health/osmosis-shared/adobeAnalytics';

export { getAdobeAnalyticsUser };
export type { AdobeAnalyticsUser };

const ADOBE_PRODUCT_NAME = 'osm';
const DEFAULT_ADOBE_BUSINESS_UNIT = 'ELS:NH';
const DEFAULT_ADOBE_ACCESS_TYPE = 'ae:anon_guest';
export const ADOBE_ONBOARDING_START_PAGE_NAME = 'onboarding:start';
export const ADOBE_ONBOARDING_STEP1_PERSONA_COMPLETE_PAGE_NAME =
  'onboarding:step1:persona:complete';
export const ADOBE_ONBOARDING_STEP2_STUDY_AREA_COMPLETE_PAGE_NAME =
  'osm:onboarding:step2:study-area:complete';
export const ADOBE_ONBOARDING_STEP3_INSTITUTION_COMPLETE_PAGE_NAME =
  'osm:onboarding:step3:institution:complete';
export const ADOBE_ONBOARDING_STEP4_PERSONALIZATION_COMPLETE_PAGE_NAME =
  'osm:onboarding:step4:personalization:complete';

type AdobeEnvironment = 'dev' | 'cert' | 'prod';

export type AdobePageData = {
  page: {
    environment: AdobeEnvironment;
    name: string;
    productName: string;
    businessUnit: string;
  };
  visitor: {
    accessType: string;
    accountId?: string;
    accountName?: string;
    userId?: string;
  };
};

export type AdobeAppDataEvent = {
  event: 'pageLoad' | 'newPage';
  page: AdobePageData['page'];
  visitor: AdobePageData['visitor'];
};

type AdobeFormStartType = 'onboarding';
export type AdobeOnboardingStep = '1' | '2' | '3' | '4';

export type AdobeFormStartEvent = {
  event: 'formStart';
  form: {
    productName: typeof ADOBE_PRODUCT_NAME;
    type: AdobeFormStartType;
  };
  page: AdobePageData['page'];
  visitor: AdobePageData['visitor'];
};

export type AdobeOnboardingSelection = {
  persona?: string;
  product?: string;
  schoolID?: string | number;
  status?: string;
  topicsSelected?: number;
};

export type AdobeFormSubmitEvent = {
  event: 'formSubmit';
  form: {
    productName: typeof ADOBE_PRODUCT_NAME;
    type: AdobeFormStartType;
    step: AdobeOnboardingStep;
  } & AdobeOnboardingSelection;
  page: AdobePageData['page'];
  visitor: AdobePageData['visitor'];
};

type AdobePageInput = {
  pathname?: string | null;
  asPath?: string | null;
  pageName?: string;
  user?: AdobeAnalyticsUser | null;
};

/**
 * Returns whether Adobe Analytics has enough config to run.
 */
export const isAdobeAnalyticsEnabled = (): boolean =>
  process.env.NEXT_PUBLIC_ADOBE_ANALYTICS_ENABLED === 'true' &&
  Boolean(process.env.NEXT_PUBLIC_ADOBE_LAUNCH_URL);

/**
 * Reads the configured Adobe Launch script URL.
 */
export const getAdobeLaunchUrl = (): string =>
  process.env.NEXT_PUBLIC_ADOBE_LAUNCH_URL || '';

/**
 * Reads the configured Adobe business unit value.
 */
export const getAdobeBusinessUnit = (): string =>
  process.env.NEXT_PUBLIC_ADOBE_ANALYTICS_BUSINESS_UNIT ||
  DEFAULT_ADOBE_BUSINESS_UNIT;

/**
 * Resolves the Adobe environment label for the current runtime.
 */
export const getAdobeEnvironment = (): AdobeEnvironment => {
  const configuredEnvironment =
    process.env.NEXT_PUBLIC_ADOBE_ANALYTICS_ENVIRONMENT;

  if (configuredEnvironment === 'prod') return 'prod';
  if (configuredEnvironment === 'cert') return 'cert';
  if (configuredEnvironment === 'dev') return 'dev';

  return process.env.NODE_ENV === 'production' ? 'prod' : 'dev';
};

const getPathWithoutQuery = ({ pathname, asPath }: AdobePageInput): string => {
  const candidate = asPath || pathname || '/';
  return candidate.split(/[?#]/)[0] || '/';
};

const ADOBE_PAGE_NAME_OVERRIDES: Record<string, string> = {
  start: ADOBE_ONBOARDING_START_PAGE_NAME,
};

/**
 * Converts the current route into the Adobe page name.
 *
 * Adobe Launch derives the product prefix from page.productName; including it
 * here causes doubled page names such as osm:osm:account:profile.
 */
export const getAdobePageName = (input: AdobePageInput): string => {
  const path = getPathWithoutQuery(input)
    .split('/')
    .filter(Boolean)
    .join(':')
    .toLowerCase();

  return ADOBE_PAGE_NAME_OVERRIDES[path] ?? (path || 'home');
};

/**
 * Maps the current user state to Adobe's standard access type values.
 */
export const getAdobeAccessType = (user?: AdobePageInput['user']): string => {
  if (!user?.userID) return DEFAULT_ADOBE_ACCESS_TYPE;

  const primeStatus = String(user.prime?.statusString || '').toLowerCase();

  if (
    user.hasPaid ||
    user.prime?.hasPaid ||
    ['subscription', 'paid prime', 'prime'].includes(primeStatus)
  ) {
    return 'ae:reg_user';
  }

  return 'ae:reg_guest';
};

/**
 * Builds the Table 1 Adobe pageData payload for the current page.
 */
export const buildAdobePageData = (input: AdobePageInput): AdobePageData => ({
  page: {
    environment: getAdobeEnvironment(),
    name: input.pageName ?? getAdobePageName(input),
    productName: ADOBE_PRODUCT_NAME,
    businessUnit: getAdobeBusinessUnit(),
  },
  visitor: {
    accessType: getAdobeAccessType(input.user),
    ...getAdobeVisitorIdentifiers(input.user),
  },
});

/**
 * Serializes the Adobe pageData setup script.
 */
export const getAdobeDataLayerScript = (pageData: AdobePageData): string => {
  const serializedPageData = JSON.stringify(pageData).replace(/</g, '\\u003c');

  return `
    window.pageData = ${serializedPageData};
    window.appData = window.appData || [];
    window.__osmosisAdobeAnalytics = window.__osmosisAdobeAnalytics || {
      initialPageLoadTracked: false
    };
  `;
};

/**
 * Builds the shared Adobe bootstrap script for head placement.
 */
export const getAdobeBootstrapScript = (): string => `
  window.appData = window.appData || [];
  window.__osmosisAdobeAnalytics = window.__osmosisAdobeAnalytics || {
    initialPageLoadTracked: false
  };
`;

/**
 * Builds the inline script that queues the initial Adobe pageLoad event.
 */
export const getAdobePageLoadScript = (): string => `
  window.appData = window.appData || [];
  window.__osmosisAdobeAnalytics = window.__osmosisAdobeAnalytics || {
    initialPageLoadTracked: false
  };

  if (
    !window.__osmosisAdobeAnalytics.initialPageLoadTracked &&
    window.pageData &&
    window.pageData.page &&
    window.pageData.visitor
  ) {
    window.appData.push({
      event: 'pageLoad',
      page: window.pageData.page,
      visitor: window.pageData.visitor
    });

    window.__osmosisAdobeAnalytics.initialPageLoadTracked = true;
  }
`;

const setWindowPageData = (pageData: AdobePageData): void => {
  window.pageData = pageData;
  window.appData = window.appData || [];
  window.__osmosisAdobeAnalytics = window.__osmosisAdobeAnalytics || {
    initialPageLoadTracked: false,
  };
};

/**
 * Pushes the initial Adobe pageLoad event once for the current document.
 */
export const pushAdobePageLoadOnce = (pageData: AdobePageData): void => {
  if (typeof window === 'undefined') return;

  setWindowPageData(pageData);

  const trackerState = window.__osmosisAdobeAnalytics;
  if (trackerState.initialPageLoadTracked) return;

  window.appData.push({
    event: 'pageLoad',
    page: pageData.page,
    visitor: pageData.visitor,
  });
  trackerState.initialPageLoadTracked = true;
};

/**
 * Pushes an Adobe newPage event for a client-side route change.
 */
export const pushAdobeNewPage = (pageData: AdobePageData): void => {
  if (typeof window === 'undefined') return;

  setWindowPageData(pageData);

  window.appData.push({
    event: 'newPage',
    page: pageData.page,
    visitor: pageData.visitor,
  });
};

/**
 * Pushes an Adobe formStart event for the onboarding funnel.
 */
export const pushAdobeFormStart = (
  pageData: AdobePageData,
  formType: AdobeFormStartType,
): void => {
  if (typeof window === 'undefined') return;
  if (!pageData.visitor.userId) return;

  setWindowPageData(pageData);

  const formStartEvent: AdobeFormStartEvent = {
    event: 'formStart',
    form: {
      productName: ADOBE_PRODUCT_NAME,
      type: formType,
    },
    page: pageData.page,
    visitor: pageData.visitor,
  };

  window.appData.push(formStartEvent);
};

/**
 * Pushes an Adobe formSubmit event for an onboarding step completion.
 */
export const pushAdobeOnboardingStepComplete = (
  pageData: AdobePageData,
  step: AdobeOnboardingStep,
  selection?: AdobeOnboardingSelection,
): void => {
  if (typeof window === 'undefined') return;
  if (!pageData.visitor.userId) return;

  setWindowPageData(pageData);

  const formSubmitEvent: AdobeFormSubmitEvent = {
    event: 'formSubmit',
    form: {
      productName: ADOBE_PRODUCT_NAME,
      type: 'onboarding',
      step,
      ...selection,
    },
    page: pageData.page,
    visitor: pageData.visitor,
  };

  window.appData.push(formSubmitEvent);
};

/**
 * Preserves the Step 1 helper API while using the shared completion payload.
 */
export const pushAdobeOnboardingStep1Complete = (
  pageData: AdobePageData,
  selection?: AdobeOnboardingSelection,
): void => pushAdobeOnboardingStepComplete(pageData, '1', selection);
